import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  Switch,
  Grid,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { coachingProgramAPI, memberProfileAPI, amenityAPI } from '../../services/api';
import { CoachingProgram } from '../../types/coaching';
import { MemberProfile } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

interface ProgramInstance {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  venues?: string[];
  capacity: number;
  enrolledCount?: number;
  availableSpots?: number;
  isFull?: boolean;
}

interface EnrollMemberToProgramDialogProps {
  open: boolean;
  onClose: () => void;
  program: CoachingProgram | null;
  onSuccess: (message?: string) => void;
}

const EnrollMemberToProgramDialog: React.FC<EnrollMemberToProgramDialogProps> = ({
  open,
  onClose,
  program,
  onSuccess
}) => {
  // Fetch active member profiles
  const { profiles: memberProfiles, loading: loadingMembers, refetch: refetchMemberProfiles } = useActiveMemberProfiles({ autoLoad: false });
  
  const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
  const [notes, setNotes] = useState('');
  const [isGuestEnrollment, setIsGuestEnrollment] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Drop-in enrollment state
  const [isDropIn, setIsDropIn] = useState(false);
  const [programInstances, setProgramInstances] = useState<ProgramInstance[]>([]);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());

  // Fetch member profiles and instances when dialog opens
  useEffect(() => {
    if (open) {
      refetchMemberProfiles();
      fetchAmenities();
      if (program && program.allowDropIns) {
        fetchProgramInstances();
      }
      resetForm();
    }
  }, [open, program]);

  const resetForm = () => {
    setSelectedMember(null);
    setNotes('');
    setIsGuestEnrollment(false);
    setGuestName('');
    setGuestEmail('');
    setIsDropIn(false);
    setSelectedInstanceIds([]);
    setError(null);
  };



  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      const names = new Map<string, string>();
      response.data.forEach((amenity: any) => {
        names.set(amenity.id, amenity.name);
      });
      setVenueNames(names);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      // Don't show error - not critical
    }
  };

  const fetchProgramInstances = async () => {
    if (!program) return;

    try {
      setLoadingInstances(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await coachingProgramAPI.getProgramInstances(
        program.id, 
        { startDate: today, includeEnrollments: 'true' }
      );
      setProgramInstances(response.data || []);
    } catch (error: any) {
      console.error('Error fetching program instances:', error);
      // Don't show error - drop-in might not be available
    } finally {
      setLoadingInstances(false);
    }
  };

  const handleInstanceToggle = (instanceId: string) => {
    setSelectedInstanceIds((prev) =>
      prev.includes(instanceId)
        ? prev.filter((id) => id !== instanceId)
        : [...prev, instanceId]
    );
  };

  const getVenueNames = (venueIds?: string[]): string => {
    if (!venueIds || venueIds.length === 0) return 'No venue';
    return venueIds
      .map(id => venueNames.get(id) || id)
      .join(', ');
  };

  const calculateTotalCost = (): number => {
    if (!program) return 0;
    if (isDropIn) {
      return (program.dropInCost || 0) * selectedInstanceIds.length;
    }
    return program.totalCost || 0;
  };

  const handleEnroll = async () => {
    if (!program) return;

    // Validation
    if (!isGuestEnrollment && !selectedMember) {
      setError('Please select a member to enroll');
      return;
    }

    if (isGuestEnrollment && (!guestName.trim() || !guestEmail.trim())) {
      setError('Please provide guest name and email');
      return;
    }

    if (isGuestEnrollment && !guestEmail.includes('@')) {
      setError('Please provide a valid email address');
      return;
    }

    if (isDropIn && selectedInstanceIds.length === 0) {
      setError('Please select at least one session');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const enrollmentData = {
        memberNotes: notes,
        memberProfileId: !isGuestEnrollment ? selectedMember?.id : undefined,
        isGuest: isGuestEnrollment,
        guestName: isGuestEnrollment ? guestName : undefined,
        guestEmail: isGuestEnrollment ? guestEmail : undefined,
        enrollmentType: isDropIn ? 'DROP_IN' : 'FULL_PROGRAM',
        instanceIds: isDropIn ? selectedInstanceIds : undefined
      };

      const response = await coachingProgramAPI.enrollInCoachingProgram(program.id, enrollmentData);

      // Close dialog immediately and then call onSuccess with the message
      onClose();
      
      // Delay the onSuccess call to allow dialog to close cleanly
      setTimeout(() => {
        onSuccess(response.data.message || 'Successfully enrolled in coaching program');
      }, 100);
      
    } catch (error: any) {
      console.error('Error enrolling in coaching program:', error);
      const errorMessage = error.response?.data?.message || 'Failed to enroll in coaching program';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!program) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown={true}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon />
          <Box>
            <Typography variant="h6">
              Enroll in Coaching Program
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {program.name} • {new Date(program.startDate).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Guest/Member Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={isGuestEnrollment}
                onChange={(e) => setIsGuestEnrollment(e.target.checked)}
                disabled={loading}
              />
            }
            label="Enroll as Guest"
          />

          {/* Member Selection OR Guest Information */}
          {!isGuestEnrollment ? (
            <FormControl fullWidth>
              <Autocomplete
                options={memberProfiles}
                value={selectedMember}
                onChange={(event, newValue) => setSelectedMember(newValue)}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                getOptionKey={(option) => option.id}
                loading={loadingMembers}
                disabled={loading}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={key} {...otherProps}>
                      <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2">
                          {option.firstName} {option.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email} • {option.membershipTier?.name}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Member"
                    placeholder="Search for a member..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMembers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Guest Name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Guest Email"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                />
              </Grid>
            </Grid>
          )}

          {/* Drop-In Toggle (only show if drop-ins are allowed) */}
          {program.allowDropIns && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDropIn}
                    onChange={(e) => {
                      setIsDropIn(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedInstanceIds([]);
                      }
                    }}
                    disabled={loading}
                  />
                }
                label="Drop-In Session(s)"
              />
              {isDropIn && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  Select individual sessions to attend (${program.dropInCost} per session)
                </Typography>
              )}
            </Box>
          )}

          {/* Instance Selection (only show for drop-in) */}
          {isDropIn && (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon fontSize="small" />
                Select Sessions
              </Typography>
              {loadingInstances ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : programInstances.length === 0 ? (
                <Alert severity="info">No upcoming sessions available</Alert>
              ) : (
                <List sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                  {programInstances.map((instance) => (
                    <ListItem key={instance.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleInstanceToggle(instance.id)}
                        disabled={instance.isFull}
                        dense
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedInstanceIds.includes(instance.id)}
                            disabled={instance.isFull}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2">
                                {formatDate(instance.sessionDate)} • {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                              </Typography>
                              {instance.isFull && (
                                <Chip label="Full" color="error" size="small" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {getVenueNames(instance.venues)} • {instance.enrolledCount || 0}/{instance.capacity} enrolled
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
              {selectedInstanceIds.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedInstanceIds.length} session{selectedInstanceIds.length > 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          )}

          {/* Notes */}
          <TextField
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            placeholder="Add any notes about this enrollment..."
          />

          {/* Program Info Summary */}
          {(selectedMember || (isGuestEnrollment && guestName && guestEmail)) && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enrollment Summary:
              </Typography>
              <Typography variant="body2">
                <strong>
                  {isGuestEnrollment ? 'Guest' : 'Member'}:
                </strong>{' '}
                {isGuestEnrollment 
                  ? `${guestName} (${guestEmail})`
                  : `${selectedMember?.firstName} ${selectedMember?.lastName}`
                }
              </Typography>
              <Typography variant="body2">
                <strong>Program:</strong> {program.name}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {isDropIn ? `Drop-In (${selectedInstanceIds.length} session${selectedInstanceIds.length !== 1 ? 's' : ''})` : 'Full Program'}
              </Typography>
              <Typography variant="body2">
                <strong>Coach:</strong> {program.coachName}
              </Typography>
              {!isDropIn && (
                <>
                  <Typography variant="body2">
                    <strong>Capacity:</strong> {program.currentEnrollments || 0}/{program.capacity}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duration:</strong> {new Date(program.startDate).toLocaleDateString()} - {new Date(program.endDate).toLocaleDateString()}
                  </Typography>
                </>
              )}
              {calculateTotalCost() > 0 && (
                <Typography variant="body2">
                  <strong>Cost:</strong> ${calculateTotalCost()}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleEnroll}
          variant="contained"
          disabled={
            loading || 
            (!selectedMember && !isGuestEnrollment) || 
            (isGuestEnrollment && (!guestName.trim() || !guestEmail.trim())) ||
            (isDropIn && selectedInstanceIds.length === 0)
          }
          startIcon={loading ? <CircularProgress size={20} /> : <GroupIcon />}
        >
          {loading ? 'Enrolling...' : 'Enroll'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnrollMemberToProgramDialog;
